import { Controller } from "@hotwired/stimulus"

// Coordinate with the toolbar's "Alle ein-/ausklappen" button, which broadcasts these
// document-level events (same decoupling as planning:sort-by-proximity). ONE shared listener
// pair fans the broadcast out to a registry of live instances — a wide board renders up to
// ~200 site sections, and 2 document listeners per section made every Turbo render pay for
// re-registering all of them.
const sections = new Set()
const expandAll = () => sections.forEach((section) => section.setExpanded(true))
const collapseAll = () => sections.forEach((section) => section.setExpanded(false))

function register(section) {
  if (sections.size === 0) {
    document.addEventListener("planning:expand-all", expandAll)
    document.addEventListener("planning:collapse-all", collapseAll)
  }
  sections.add(section)
}

function unregister(section) {
  sections.delete(section)
  if (sections.size === 0) {
    document.removeEventListener("planning:expand-all", expandAll)
    document.removeEventListener("planning:collapse-all", collapseAll)
  }
}

// Collapsible per-site section on the capacity-planning board. Persists the
// collapsed/expanded state per site in localStorage (mirrors plantafel-site-section).
export default class extends Controller {
  static targets = ["content", "icon"]
  static values = { sectionId: String }

  static STORAGE_KEY = "planning_section_states"

  connect() {
    this.expanded = this.loadState()
    this.applyState()
    register(this)
  }

  disconnect() {
    unregister(this)
  }

  loadState() {
    try {
      const saved = localStorage.getItem(this.constructor.STORAGE_KEY)
      const states = saved ? JSON.parse(saved) : {}
      return states[this.sectionIdValue] !== false
    } catch (_e) {
      return true
    }
  }

  saveState() {
    try {
      const saved = localStorage.getItem(this.constructor.STORAGE_KEY)
      const states = saved ? JSON.parse(saved) : {}
      states[this.sectionIdValue] = this.expanded
      localStorage.setItem(this.constructor.STORAGE_KEY, JSON.stringify(states))
    } catch (_e) {
      // Ignore storage errors
    }
  }

  applyState() {
    if (this.hasContentTarget) {
      this.contentTarget.classList.toggle("hidden", !this.expanded)
    }
    if (this.hasIconTarget) {
      this.iconTarget.style.transform = this.expanded ? "rotate(0deg)" : "rotate(-90deg)"
    }
  }

  toggle() {
    this.setExpanded(!this.expanded)
  }

  setExpanded(expanded) {
    if (this.expanded === expanded) return
    this.expanded = expanded
    this.applyState()
    this.saveState()
  }

  // Ask the resource panel to sort employees by air distance to this section's site. The
  // panel is a separate fixed drawer, so this goes through a document-level event (same
  // pattern as planning:plan-range → planning-allocation-dialog).
  sortByProximity(event) {
    event.stopPropagation()
    const { siteId, siteName, lat, lng } = event.currentTarget.dataset
    document.dispatchEvent(
      new CustomEvent("planning:sort-by-proximity", {
        detail: { siteId, siteName, lat: parseFloat(lat), lng: parseFloat(lng) },
      }),
    )
  }
}
